import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Sparkles, Clock, FileText, Users, Crown, Loader2, Plus, Minus, Tag, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import Layout from '../components/Layout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PlanFeature = ({ included, children }) => (
  <li className={`flex items-center gap-2 ${included ? 'text-foreground' : 'text-muted-foreground line-through'}`}>
    <Check className={`h-4 w-4 ${included ? 'text-green-500' : 'text-muted-foreground'}`} />
    {children}
  </li>
);

const PlanCard = ({ plan, isYearly, currentPlan, onSelect, onStartTrial, isLoading, extraUsers, onExtraUsersChange }) => {
  const basePrice = isYearly ? plan.yearly_price : plan.monthly_price;
  const extraUserPrice = isYearly ? plan.extra_user_yearly : plan.extra_user_monthly;
  const totalPrice = basePrice + (extraUserPrice * extraUsers);
  const monthlyEquivalent = isYearly ? (totalPrice / 12).toFixed(2) : totalPrice.toFixed(2);
  const isCurrentPlan = currentPlan === plan.plan;
  const isFree = plan.plan === 'free';
  const isPro = plan.plan === 'pro';
  const isBusiness = plan.plan === 'business';
  
  const getPlanIcon = () => {
    if (isBusiness) return <Crown className="h-6 w-6 text-yellow-500" />;
    if (isPro) return <Sparkles className="h-6 w-6 text-blue-500" />;
    return <Users className="h-6 w-6 text-gray-500" />;
  };

  return (
    <Card className={`relative ${isBusiness ? 'border-yellow-500 border-2' : ''} ${isCurrentPlan ? 'ring-2 ring-primary' : ''}`}>
      {isBusiness && (
        <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-yellow-500">
          Empfohlen
        </Badge>
      )}
      {isCurrentPlan && (
        <Badge className="absolute -top-3 right-4 bg-green-500">
          Aktuell
        </Badge>
      )}
      <CardHeader className="text-center pb-2">
        <div className="flex justify-center mb-2">{getPlanIcon()}</div>
        <CardTitle className="text-2xl">{plan.name}</CardTitle>
        <CardDescription>
          {isFree && '3 Projekte, 1 User'}
          {isPro && 'Zeiterfassung, ∞ Projekte'}
          {isBusiness && 'Alle Features + KI'}
        </CardDescription>
      </CardHeader>
      <CardContent className="text-center">
        <div className="mb-4">
          <span className="text-4xl font-bold">CHF {monthlyEquivalent}</span>
          <span className="text-muted-foreground">/Monat</span>
          {isYearly && !isFree && (
            <p className="text-sm text-green-600 mt-1">
              CHF {totalPrice.toFixed(2)}/Jahr (20% gespart)
            </p>
          )}
          {extraUsers > 0 && !isFree && (
            <p className="text-xs text-muted-foreground mt-1">
              inkl. {extraUsers} Extra-User
            </p>
          )}
        </div>
        
        <ul className="space-y-2 text-left text-sm">
          <PlanFeature included={true}>
            {plan.features.max_projects === -1 ? 'Unbegrenzte Projekte' : `${plan.features.max_projects} Projekte`}
          </PlanFeature>
          <PlanFeature included={true}>
            {plan.features.max_users + extraUsers} User {extraUsers > 0 ? `(1 + ${extraUsers})` : 'inklusive'}
          </PlanFeature>
          <PlanFeature included={plan.features.time_tracking}>
            <Clock className="h-4 w-4 inline mr-1" /> Zeiterfassung
          </PlanFeature>
          <PlanFeature included={plan.features.risk_management}>
            Risikomanagement
          </PlanFeature>
          <PlanFeature included={plan.features.resource_planning}>
            Ressourcenplanung
          </PlanFeature>
          <PlanFeature included={plan.features.change_management}>
            Change Management
          </PlanFeature>
          <PlanFeature included={plan.features.requirements_catalog}>
            Anforderungskatalog
          </PlanFeature>
          <PlanFeature included={plan.features.ai_features}>
            <Sparkles className="h-4 w-4 inline mr-1" /> KI-Features
          </PlanFeature>
          <PlanFeature included={plan.features.advanced_reports}>
            <FileText className="h-4 w-4 inline mr-1" /> Erweiterte Reports
          </PlanFeature>
          <PlanFeature included={plan.features.priority_support}>
            Priority Support
          </PlanFeature>
        </ul>
        
        {/* Extra Users Selector */}
        {!isFree && !isCurrentPlan && (
          <div className="mt-4 p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Extra User</span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => onExtraUsersChange(plan.plan, Math.max(0, extraUsers - 1))}
                  disabled={extraUsers === 0}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <span className="w-8 text-center font-bold">{extraUsers}</span>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => onExtraUsersChange(plan.plan, extraUsers + 1)}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              + CHF {isYearly ? (extraUserPrice / 12).toFixed(2) : extraUserPrice.toFixed(2)}/User/Monat
            </p>
          </div>
        )}
      </CardContent>
      <CardFooter className="flex flex-col gap-2">
        {isCurrentPlan ? (
          <Button disabled className="w-full" variant="outline">
            Aktueller Plan
          </Button>
        ) : isFree ? (
          <Button disabled className="w-full" variant="outline">
            Kostenlos
          </Button>
        ) : (
          <>
            <Button 
              className="w-full" 
              onClick={() => onSelect(plan.plan, extraUsers)}
              disabled={isLoading}
            >
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {isBusiness ? 'Business wählen' : 'Pro wählen'}
            </Button>
            {(isPro || isBusiness) && currentPlan === 'free' && (
              <Button 
                variant="outline" 
                className={`w-full ${isBusiness ? 'text-yellow-600' : 'text-blue-600'}`}
                onClick={() => onStartTrial(plan.plan)}
                disabled={isLoading}
              >
                14 Tage kostenlos testen
              </Button>
            )}
          </>
        )}
      </CardFooter>
    </Card>
  );
};

export default function Subscription() {
  const [plans, setPlans] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [features, setFeatures] = useState(null);
  const [isYearly, setIsYearly] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [extraUsers, setExtraUsers] = useState({ pro: 0, business: 0 });
  const [promoCode, setPromoCode] = useState('');
  const [promoValidation, setPromoValidation] = useState(null);
  const [isValidatingPromo, setIsValidatingPromo] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleExtraUsersChange = (plan, count) => {
    setExtraUsers(prev => ({ ...prev, [plan]: count }));
  };

  const validatePromoCode = async (plan) => {
    if (!promoCode.trim()) {
      setPromoValidation(null);
      return;
    }
    
    setIsValidatingPromo(true);
    const token = localStorage.getItem('token');
    
    try {
      const response = await fetch(
        `${API_URL}/api/promo-codes/validate/${promoCode}?plan=${plan}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setPromoValidation(data);
        if (data.valid) {
          toast.success(data.message);
        } else {
          toast.error(data.message);
        }
      }
    } catch (error) {
      console.error('Error validating promo code:', error);
    } finally {
      setIsValidatingPromo(false);
    }
  };

  const clearPromoCode = () => {
    setPromoCode('');
    setPromoValidation(null);
  };

  const fetchData = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const [plansRes, subRes, featuresRes] = await Promise.all([
        fetch(`${API_URL}/api/subscriptions/plans`),
        fetch(`${API_URL}/api/subscriptions/current`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/subscriptions/features`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (plansRes.ok) setPlans(await plansRes.json());
      if (subRes.ok) setCurrentSubscription(await subRes.json());
      if (featuresRes.ok) setFeatures(await featuresRes.json());
    } catch (error) {
      console.error('Error fetching subscription data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Check for return from Stripe checkout
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const token = localStorage.getItem('token');

    if (attempts >= maxAttempts) {
      toast.warning('Bitte überprüfen Sie Ihre E-Mail für die Bestätigung.');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/subscriptions/checkout/status/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.payment_status === 'paid') {
          toast.success('Zahlung erfolgreich! Ihr Abonnement wurde aktiviert.');
          // Clear URL params and refresh data
          navigate('/subscription', { replace: true });
          fetchData();
          return;
        } else if (data.status === 'expired') {
          toast.error('Session abgelaufen. Bitte versuchen Sie es erneut.');
          navigate('/subscription', { replace: true });
          return;
        }
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  };

  const handleSelectPlan = async (planId, extraUserCount) => {
    setIsProcessing(true);
    const token = localStorage.getItem('token');

    try {
      const response = await fetch(`${API_URL}/api/subscriptions/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          plan: planId,
          billing_interval: isYearly ? 'yearly' : 'monthly',
          extra_users: extraUserCount || 0,
          origin_url: window.location.origin,
          promo_code: promoValidation?.valid ? promoCode : null
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Show discount info if applied
        if (data.discount_applied) {
          toast.success(`Rabatt angewendet: ${data.discount_applied}`);
        }
        
        // Redirect to Stripe Checkout
        window.location.href = data.checkout_url;
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Checkout konnte nicht gestartet werden.');
      }
    } catch (error) {
      toast.error('Ein Fehler ist aufgetreten.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleStartTrial = async (planId = 'pro') => {
    setIsProcessing(true);
    const token = localStorage.getItem('token');

    try {
      const response = await fetch(`${API_URL}/api/subscriptions/start-trial`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({ plan: planId })
      });

      if (response.ok) {
        const data = await response.json();
        const planName = data.plan === 'business' ? 'Business' : 'Pro';
        toast.success(`Trial gestartet! Sie haben 14 Tage ${planName}-Zugang bis ${new Date(data.trial_end).toLocaleDateString('de-DE')}.`);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Trial konnte nicht gestartet werden.');
      }
    } catch (error) {
      toast.error('Ein Fehler ist aufgetreten.');
    } finally {
      setIsProcessing(false);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container max-w-6xl py-8">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-2">Wählen Sie Ihren Plan</h1>
          <p className="text-muted-foreground">
            Starten Sie mit einem kostenlosen Trial oder wählen Sie einen Plan.
          </p>
          
          {features?.is_trial && (
            <Badge variant="outline" className="mt-4 text-blue-600 border-blue-600">
              🎉 Trial aktiv - noch {features.trial_days_remaining} Tage
            </Badge>
          )}
        </div>

        {/* Billing Toggle */}
        <div className="flex items-center justify-center gap-4 mb-8">
          <Label htmlFor="billing-toggle" className={!isYearly ? 'font-bold' : ''}>Monatlich</Label>
          <Switch
            id="billing-toggle"
            checked={isYearly}
            onCheckedChange={setIsYearly}
          />
          <Label htmlFor="billing-toggle" className={isYearly ? 'font-bold' : ''}>
            Jährlich <Badge variant="secondary" className="ml-1">20% sparen</Badge>
          </Label>
        </div>

        {/* Promo Code Input */}
        <div className="max-w-md mx-auto mb-8">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Tag className="h-4 w-4 text-muted-foreground" />
                <Label className="text-sm font-medium">Rabattcode</Label>
              </div>
              <div className="flex gap-2 mt-2">
                <div className="relative flex-1">
                  <Input
                    placeholder="Code eingeben (z.B. BLACKFRIDAY)"
                    value={promoCode}
                    onChange={(e) => {
                      setPromoCode(e.target.value.toUpperCase());
                      // Clear validation when code changes
                      if (promoValidation) setPromoValidation(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && promoCode.trim()) {
                        e.preventDefault();
                        validatePromoCode('pro'); // Validate for pro by default
                      }
                    }}
                    className={promoValidation?.valid ? 'border-green-500 pr-8' : ''}
                  />
                  {promoValidation?.valid && (
                    <Check className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />
                  )}
                </div>
                <Button 
                  variant="outline" 
                  onClick={() => validatePromoCode('pro')}
                  disabled={!promoCode.trim() || isValidatingPromo}
                >
                  {isValidatingPromo ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Prüfen'
                  )}
                </Button>
                {promoCode && (
                  <Button variant="ghost" size="icon" onClick={clearPromoCode}>
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              {promoValidation && (
                <p className={`text-sm mt-2 ${promoValidation.valid ? 'text-green-600' : 'text-red-600'}`}>
                  {promoValidation.valid 
                    ? `✓ ${promoValidation.discount_display} Rabatt wird angewendet`
                    : promoValidation.message
                  }
                </p>
              )}
              <p className="text-xs text-muted-foreground mt-2">
                Code eingeben und Enter drücken oder auf &quot;Prüfen&quot; klicken.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Plan Cards */}
        <div className="grid md:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <PlanCard
              key={plan.plan}
              plan={plan}
              isYearly={isYearly}
              currentPlan={features?.plan || 'free'}
              onSelect={(planId, extra) => {
                // Validate promo code for this plan before checkout
                if (promoCode && !promoValidation?.valid) {
                  validatePromoCode(planId);
                }
                handleSelectPlan(planId, extra);
              }}
              onStartTrial={handleStartTrial}
              isLoading={isProcessing}
              extraUsers={extraUsers[plan.plan] || 0}
              onExtraUsersChange={handleExtraUsersChange}
            />
          ))}
        </div>

        {/* Current Subscription Info */}
        {currentSubscription && currentSubscription.plan !== 'free' && (
          <Card className="mt-8">
            <CardHeader>
              <CardTitle>Aktuelles Abonnement</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Plan</p>
                  <p className="font-medium">{currentSubscription.plan.toUpperCase()}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <Badge variant={currentSubscription.status === 'active' ? 'default' : 'secondary'}>
                    {currentSubscription.status === 'trialing' ? 'Trial' : 'Aktiv'}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Gültig bis</p>
                  <p className="font-medium">
                    {new Date(currentSubscription.current_period_end).toLocaleDateString('de-DE')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
